// USA citrus cold treatment (R-A6, PPECB HP05 rev 9) - the client-side rules.
//
// Ready is COMPUTED on the server (cold_treatment_status, mig 20261129500000) so the tab, the
// printout and the dispatch warning share one answer. This file holds the types for that answer,
// the input rules for the forms, the "in-date probe" test, and the dispatch warning text.
import type { QueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { withTimeout } from '@/lib/saveTimeout'
import type { DispatchWarning } from '@/lib/inspectionAge'
import { sanitizeTempInput } from '@/lib/coldroomTemps'

export const DEFAULT_REQUIRED_HOURS = 72
export const DEFAULT_FINAL_HOURS = 24
export const MIN_REQUIRED_HOURS = 72
export const MAX_REQUIRED_HOURS = 720
export const MIN_FINAL_HOURS = 24
export const THRESHOLD_MIN = -5
export const THRESHOLD_MAX = 10

export interface ColdTreatmentLot {
  lot_id: string
  lot_no: string
  coldroom_id: string
  threshold_c: number
  required_hours: number
  final_hours: number
  note: string | null
  started_at: string
  recorded_by: string
}

export interface ColdTreatmentProbe {
  probe_id: string
  lot_id: string
  probe_no: number
  equipment_id: string
  pallet_id: string
  position: string
  calibration_due_on: string
  retired_at: string | null
  retired_reason: string | null
  retired_by: string | null
  created_at: string
}

export interface ColdTreatmentReading {
  reading_id: string
  lot_id: string
  probe_id: string
  read_at: string
  pulp_temp_c: number
  note: string | null
  recorded_by: string
  supersedes_id: string | null
}

export interface ColdTreatmentProbeStatus {
  probe_id: string
  probe_no: number
  retired: boolean
  missed_hours?: number
  window_missed?: number
  window_over?: number
  last_pulp_c?: number | null
  last_read_at?: string | null
  window_ok?: boolean
}

/** What cold_treatment_status returns. */
export interface ColdTreatmentStatus {
  lot_id: string
  ready: boolean
  reasons: string[]
  elapsed_hours: number
  required_hours: number
  final_hours: number
  threshold_c: number
  live_probes: number
  min_probes: number
  pallets: number
  probes: ColdTreatmentProbeStatus[]
  /** The whole hour the treatment finished (Ready latches there). Null while the lot is not Ready. */
  ready_at?: string | null
  /** The instant the verdict is judged at: ready_at for a Ready lot, now otherwise. */
  judged_at?: string
}

/** A threshold such as -0.55 or -0.8. Two decimals, -5..10. Null when blank or not a number. */
export function parseThreshold(v: string): number | null {
  const t = sanitizeTempInput(v).trim()
  if (t === '' || t === '-' || t === '.' || t === '-.') return null
  const n = Number(t)
  if (!Number.isFinite(n)) return null
  const r = Math.round(n * 100) / 100
  return r >= THRESHOLD_MIN && r <= THRESHOLD_MAX ? r : null
}

/** A pulp reading, two decimals, -40..60. Null when blank, not a number or out of range. */
export function parsePulp(v: string): number | null {
  const t = sanitizeTempInput(v).trim()
  if (t === '' || t === '-' || t === '.' || t === '-.') return null
  const n = Number(t)
  if (!Number.isFinite(n)) return null
  const r = Math.round(n * 100) / 100
  return r >= -40 && r <= 60 ? r : null
}

/** The printed verdict, judged at the time the treatment finished (Ready) or at print time (Not ready). `fmt` formats an ISO time. */
export function verdictLine(s: ColdTreatmentStatus, fmt: (iso: string) => string, nowIso: string): string {
  const hours = Math.floor(s.elapsed_hours)
  if (s.ready) return `READY since ${fmt(s.ready_at ?? nowIso)} · treatment finished after ${hours} h`
  return `NOT READY at ${fmt(s.judged_at ?? nowIso)} · ${hours} h elapsed · ${s.reasons.join(' ')}`
}

export const fmtC = (n: number | null | undefined) => (n == null ? '-' : (Math.round(n * 100) / 100).toString())

/** The equipment_summary band that ComplianceEquipment shows: an item is in date unless it is overdue or has no calibration. */
export function calibrationInDate(e: { status: string; calibration_band: string | null }): boolean {
  return e.status === 'ACTIVE' && !!e.calibration_band && e.calibration_band !== 'overdue' && e.calibration_band !== 'no_cal'
}

const norm = (s: string | null | undefined) => (s ?? '').trim().toUpperCase()

/** Country codes market_regime_map files under a USA regime. Best effort: the literal US / USA always count. */
async function fetchUsaCodes(): Promise<string[]> {
  const { data, error } = await withTimeout(supabase.from('market_regime_map').select('code').eq('code_kind', 'COUNTRY').in('regime', ['US', 'USA']), 'ColdTreatment: USA codes read')
  if (error) throw error
  return ((data ?? []) as { code: string }[]).map(r => norm(r.code))
}

export interface UsaPallet {
  pallet_id: string
  target_market?: string | null
  target_country?: string | null
}

/** Is this mission going to the USA? The mission's market, or any pallet's market or country. */
export function isUsaMission(missionMarket: string | null | undefined, pallets: UsaPallet[], extraCodes: string[] = []): boolean {
  const set = new Set(['US', 'USA', ...extraCodes])
  const hit = (c: string | null | undefined) => set.has(norm(c))
  return hit(missionMarket) || pallets.some(p => hit(p.target_market) || hit(p.target_country))
}

/** Pallets of a USA mission with no Ready lot. */
function palletsWithoutReadyLot(pallets: UsaPallet[], readyIds: string[]): UsaPallet[] {
  const ok = new Set(readyIds)
  return pallets.filter(p => !ok.has(p.pallet_id))
}

export function coldTreatmentWarnings(missing: UsaPallet[]): DispatchWarning[] {
  return missing.map(p => ({
    code: 'USA_COLD_TREATMENT',
    pallet_id: p.pallet_id,
    text: `${p.pallet_id} has no Ready cold treatment lot on record. The USA needs a Ready lot.`,
  }))
}

export function coldTreatmentSummary(missing: UsaPallet[]): string {
  const n = missing.length
  return `${n} pallet${n === 1 ? ' has' : 's have'} no Ready USA cold treatment lot.`
}

/** The pallets (of these) that sit in a Ready lot. A thrown error means the check could not run. */
async function fetchReadyPallets(palletIds: string[]): Promise<string[]> {
  if (palletIds.length === 0) return []
  const { data, error } = await withTimeout(supabase.rpc('cold_treatment_ready_pallets', { p_pallet_ids: palletIds }), 'ColdTreatment: ready pallets read')
  if (error) throw error
  return (data ?? []) as string[]
}

/** The pallets that must have a Ready lot: all of them when the mission's market is the USA, else the pallets that are bound for the USA. */
export function usaBoundPallets(missionMarket: string | null | undefined, pallets: UsaPallet[], extraCodes: string[] = []): UsaPallet[] {
  const set = new Set(['US', 'USA', ...extraCodes])
  if (set.has(norm(missionMarket))) return pallets
  return pallets.filter(p => set.has(norm(p.target_market)) || set.has(norm(p.target_country)))
}

const USA_CODES_KEY = ['usa-regime-codes']

/**
 * The dispatch check. `missing` = USA-bound pallets with no Ready lot. `failed` = the lot read failed, so the
 * confirm says the check could not run (a pallet is never called treated on a failed read).
 */
export async function usaColdTreatmentCheck(
  qc: QueryClient, missionMarket: string | null | undefined, pallets: UsaPallet[],
): Promise<{ missing: UsaPallet[]; failed: boolean }> {
  const codes = await qc.fetchQuery({ queryKey: USA_CODES_KEY, queryFn: fetchUsaCodes, staleTime: 30 * 60_000 }).catch(() => [] as string[])
  const bound = usaBoundPallets(missionMarket, pallets, codes)
  if (bound.length === 0) return { missing: [], failed: false }
  try {
    const ready = await fetchReadyPallets(bound.map(p => p.pallet_id))
    return { missing: palletsWithoutReadyLot(bound, ready), failed: false }
  } catch {
    return { missing: [], failed: true }
  }
}
